/**
 * PaymentStatusCard — the manual payment status block of a clinic
 * (niv_clinic_crm, Oct 2026 model).
 *
 *  status  : לא משלם | משלם | עזב   (manual — never changes the real plan)
 *  cycle   : חודשי | תשלום חד-פעמי   (shown while status = משלם)
 *  amount  : entered with a before/after-VAT toggle; amountBeforeVat is the
 *            canonical stored figure every NiV surface displays
 *  monthly : optional הו"ק switch + charge day (feeds the briefing queue)
 *  one_time: "מתאריך" + required "פעיל עד" = clinic.planEndDate (same field;
 *            the nightly cron reverts the plan when it passes)
 *
 * Banners: payment alert (date passed / missing) + status↔plan hint with a
 * one-click fix (revert to trial / clear end date / jump to the plan block).
 */

import { useEffect, useState } from "react";
import { CreditCard, FloppyDisk, Info, Wallet, WarningCircle, Lightbulb } from "@phosphor-icons/react";
import DatePicker from "../../../../../components/ui/DatePicker";
import ConfirmDialog from "../../../../../components/ui/ConfirmDialog";
import HoverTip from "../../../../../components/ui/HoverTip";
import { useToast } from "../../../../../contexts/ToastContext";
import {
  useUpdateNivClinicCrmMutation,
  useSetClinicPlanEndDateMutation,
  useUpdateClinicPlanDirectlyMutation,
} from "../../../../../store/services/nivApi";
import {
  addVat,
  removeVat,
  formatDate,
  formatShekels,
  formatMonthKey,
  getCurrentIsraelMonthKey,
  getIsraelYmd,
  toMonthKey,
  getOneTimeMonths,
  getPaymentAlert,
  getPaymentPlanHint,
  BILLING_CYCLE_LABELS,
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_ORDER,
  VAT_PERCENT_LABEL,
} from "../nivDerived";
import { CardHead, Segmented, Switch } from "./paymentUi";
import "../ClinicPaymentPanel.css";

const STATUS_OPTIONS = PAYMENT_STATUS_ORDER.map((value) => ({ value, label: PAYMENT_STATUS_LABELS[value] }));
const STATUS_TONES = { not_paying: "muted", paying: "ok", left: "danger" };
const CYCLE_OPTIONS = Object.entries(BILLING_CYCLE_LABELS).map(([value, label]) => ({ value, label }));
const VAT_MODE_OPTIONS = [
  { value: "incl", label: 'כולל מע"מ' },
  { value: "excl", label: 'לפני מע"מ' },
];
const CHARGE_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);
const STANDING_ORDER_TIP =
  'הו"ק באשראי: ביום החיוב בכל חודש תופיע קובייה ב"בוקר טוב" עד שתאשר שהתשלום התקבל. חודשים שפספסת נערמים — אחד לכל חודש.';
const ACTIVE_UNTIL_TIP = 'זהו תאריך "בתשלום עד" של הקליניקה. בתאריך זה המסלול יורד אוטומטית לניסיון שפג.';

const todayKey = () => {
  const { year, month, day } = getIsraelYmd();
  return `${toMonthKey(year, month)}-${String(day).padStart(2, "0")}`;
};

const isoToDateKey = (iso) => (iso ? String(iso).split("T")[0] : "");

const crmToForm = (crm, clinic) => ({
  paymentStatus: crm?.paymentStatus || "not_paying",
  billingCycle: crm?.billingCycle || "monthly",
  amount: crm?.amount ?? "",
  vatMode: crm?.vatIncluded === false ? "excl" : "incl",
  isStandingOrder: !!crm?.isStandingOrder,
  chargeDay: crm?.chargeDay ?? "",
  oneTimeStartDate: crm?.oneTimeStartDate || "",
  planEndDate: isoToDateKey(clinic?.planEndDate),
});

function PaymentStatusCard({ clinic, crm, onGoToPlan }) {
  const { showSuccess, showError } = useToast();
  const clinicId = clinic?.clinicId;

  const [updateCrm, { isLoading: isSaving }] = useUpdateNivClinicCrmMutation();
  const [setPlanEndDate, { isLoading: isSavingEnd }] = useSetClinicPlanEndDateMutation();
  const [updatePlanDirectly, { isLoading: isSavingPlan }] = useUpdateClinicPlanDirectlyMutation();

  const [form, setForm] = useState(() => crmToForm(crm, clinic));
  const [confirmLeft, setConfirmLeft] = useState(false);

  // Re-init when switching clinics (not on every CRM refetch — keeps typing safe)
  useEffect(() => {
    setForm(crmToForm(crm, clinic));
    setConfirmLeft(false);
  }, [clinicId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!clinic) return null;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const busy = isSaving || isSavingEnd || isSavingPlan;

  const isPaying = form.paymentStatus === "paying";
  const isOneTime = isPaying && form.billingCycle === "one_time";
  const isMonthly = isPaying && form.billingCycle === "monthly";
  const amountNum = Number(form.amount) || 0;
  const vatIncluded = form.vatMode === "incl";
  const beforeVat = vatIncluded ? removeVat(amountNum) : amountNum;
  const withVat = vatIncluded ? amountNum : addVat(amountNum);
  const oneTimeMonths = isOneTime
    ? getOneTimeMonths({ oneTimeStartDate: form.oneTimeStartDate }, form.planEndDate || null)
    : 0;

  const row = { ...clinic, crm };
  const alert = getPaymentAlert(row);
  const hint = getPaymentPlanHint(row);

  const handleCycle = (cycle) => {
    const patch = { billingCycle: cycle };
    if (cycle === "one_time" && !form.oneTimeStartDate) patch.oneTimeStartDate = todayKey();
    set(patch);
  };

  const handleStatus = (status) => {
    if (status === "left" && form.paymentStatus !== "left") {
      setConfirmLeft(true);
      return;
    }
    set({ paymentStatus: status });
  };

  const save = async (overrides = {}) => {
    const next = { ...form, ...overrides };
    const nextPaying = next.paymentStatus === "paying";
    const nextOneTime = nextPaying && next.billingCycle === "one_time";
    const nextMonthly = nextPaying && next.billingCycle === "monthly";

    if (nextOneTime && !next.planEndDate) {
      showError('לתשלום חד-פעמי יש להגדיר תאריך "פעיל עד"');
      return false;
    }
    if (nextMonthly && next.isStandingOrder) {
      const day = Number(next.chargeDay);
      if (!Number.isInteger(day) || day < 1 || day > 28) {
        showError('בחר יום חיוב בחודש עבור הו"ק');
        return false;
      }
    }
    const amount = Number(next.amount) || 0;
    const nextVatIncluded = next.vatMode === "incl";
    try {
      await updateCrm({
        clinicId,
        updates: {
          paymentStatus: next.paymentStatus,
          leftAt: next.paymentStatus === "left" ? crm?.leftAt || new Date().toISOString() : null,
          billingCycle: nextPaying ? next.billingCycle : crm?.billingCycle || null,
          amount: next.amount === "" ? null : amount,
          vatIncluded: nextVatIncluded,
          amountBeforeVat: amount > 0 ? Math.round((nextVatIncluded ? removeVat(amount) : amount) * 100) / 100 : null,
          oneTimeStartDate: nextOneTime ? next.oneTimeStartDate || todayKey() : null,
          isStandingOrder: nextMonthly ? next.isStandingOrder : false,
          chargeDay: nextMonthly && next.isStandingOrder ? Number(next.chargeDay) : null,
          // First enable stamps the starting month; keep it on later saves
          standingOrderSince:
            nextMonthly && next.isStandingOrder ? crm?.standingOrderSince || getCurrentIsraelMonthKey() : null,
        },
      }).unwrap();

      // "פעיל עד" IS clinic.planEndDate — written only for one-time deals
      const currentEnd = isoToDateKey(clinic.planEndDate);
      if (nextOneTime && next.planEndDate !== currentEnd) {
        await setPlanEndDate({ clinicId, planEndDate: next.planEndDate }).unwrap();
      }
      showSuccess("סטטוס התשלום נשמר");
      return true;
    } catch (error) {
      console.error("[CK-9812] Error saving clinic payment status:", {
        errorCode: "CK-9812",
        errorMessage: error?.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
      showError(error?.message || "שגיאה בשמירת סטטוס התשלום");
      return false;
    }
  };

  const handleConfirmLeft = async () => {
    setConfirmLeft(false);
    set({ paymentStatus: "left" });
    await save({ paymentStatus: "left" });
  };

  // One-click fix for a status ↔ plan inconsistency
  const handleHintAction = async () => {
    if (!hint) return;
    try {
      if (hint.kind === "revertToTrial") {
        await updatePlanDirectly({ clinicId, planCode: "trial" }).unwrap();
        if (clinic.planEndDate) await setPlanEndDate({ clinicId, planEndDate: null }).unwrap();
        showSuccess("הקליניקה הורדה לניסיון");
      } else if (hint.kind === "clearEndDate") {
        await setPlanEndDate({ clinicId, planEndDate: null }).unwrap();
        set({ planEndDate: "" });
        showSuccess("תאריך הסיום נוקה");
      } else if (hint.kind === "setPaidPlan") {
        onGoToPlan?.();
      }
    } catch (error) {
      console.error("[CK-9813] Error applying payment/plan hint:", {
        errorCode: "CK-9813",
        errorMessage: error?.message,
        clinicId,
        hint: hint.kind,
        timestamp: new Date().toISOString(),
      });
      showError("הפעולה נכשלה");
    }
  };

  return (
    <section className="ncpp-card">
      <CardHead icon={Wallet} eyebrow="ניהול לקוח" title="סטטוס תשלום" />

      {alert && (
        <div className="ncpp-banner ncpp-banner-warning" role="status">
          <WarningCircle size={15} weight="fill" />
          <span>{alert.label}</span>
        </div>
      )}
      {hint && (
        <div className="ncpp-banner ncpp-banner-info" role="status">
          <Lightbulb size={15} weight="fill" />
          <span>{hint.label}</span>
          <button type="button" className="ncpp-btn ncpp-btn-secondary ncpp-btn-sm" onClick={handleHintAction} disabled={busy}>
            {hint.actionLabel}
          </button>
        </div>
      )}

      <div className="ncpp-field">
        <span className="ncpp-field-label">סטטוס</span>
        <Segmented options={STATUS_OPTIONS} value={form.paymentStatus} onChange={handleStatus} toneByValue={STATUS_TONES} />
        {form.paymentStatus === "left" && crm?.leftAt && (
          <span className="ncpp-hint">סומן כעזב ב־{formatDate(crm.leftAt)}</span>
        )}
      </div>

      {isPaying && (
        <>
          <div className="ncpp-field">
            <span className="ncpp-field-label">מחזור חיוב</span>
            <Segmented options={CYCLE_OPTIONS} value={form.billingCycle} onChange={handleCycle} />
          </div>

          <div className="ncpp-amount-row">
            <label className="ncpp-field ncpp-amount-field">
              <span className="ncpp-field-label">{isOneTime ? "סכום העסקה (₪)" : "סכום חודשי (₪)"}</span>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                value={form.amount}
                onChange={(e) => set({ amount: e.target.value })}
                placeholder="0"
              />
            </label>
            <div className="ncpp-field">
              <span className="ncpp-field-label">הסכום שהוזן הוא</span>
              <Segmented options={VAT_MODE_OPTIONS} value={form.vatMode} onChange={(v) => set({ vatMode: v })} />
            </div>
          </div>

          {amountNum > 0 && (
            <div className="ncpp-vat-line">
              {vatIncluded ? (
                <span>
                  לפני מע"מ: <strong>{formatShekels(beforeVat)}</strong>
                </span>
              ) : (
                <span>
                  כולל מע"מ: <strong>{formatShekels(withVat)}</strong>
                </span>
              )}
              <span className="ncpp-muted"> ({VAT_PERCENT_LABEL})</span>
              {isOneTime && oneTimeMonths > 0 && (
                <span className="ncpp-muted">
                  {" "}· ≈ {formatShekels(beforeVat / oneTimeMonths)} לחודש על פני {oneTimeMonths} חודשים
                </span>
              )}
            </div>
          )}

          {isMonthly && (
            <div className="ncpp-so-block">
              <Switch
                label={
                  <span className="ncpp-inline">
                    <CreditCard size={14} weight="bold" />
                    הו"ק באשראי
                    <HoverTip text={STANDING_ORDER_TIP}>
                      <Info size={12} weight="fill" className="ncpp-info" />
                    </HoverTip>
                  </span>
                }
                checked={form.isStandingOrder}
                onChange={(v) => set({ isStandingOrder: v })}
              />
              {form.isStandingOrder && (
                <label className="ncpp-field ncpp-charge-day">
                  <span className="ncpp-field-label">יום חיוב בחודש</span>
                  <select value={form.chargeDay} onChange={(e) => set({ chargeDay: e.target.value })}>
                    <option value="">בחר יום…</option>
                    {CHARGE_DAYS.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                  {crm?.standingOrderSince && (
                    <span className="ncpp-hint">מעקב מ־{formatMonthKey(crm.standingOrderSince)}</span>
                  )}
                </label>
              )}
            </div>
          )}

          {isOneTime && (
            <div className="ncpp-grid-2">
              <label className="ncpp-field">
                <span className="ncpp-field-label">מתאריך</span>
                <DatePicker value={form.oneTimeStartDate} onChange={(e) => set({ oneTimeStartDate: e.target.value })} placeholder="תחילת העסקה" />
              </label>
              <label className="ncpp-field">
                <span className="ncpp-field-label">
                  פעיל עד <span className="ncpp-required">*</span>
                  <HoverTip text={ACTIVE_UNTIL_TIP}>
                    <Info size={12} weight="fill" className="ncpp-info" />
                  </HoverTip>
                </span>
                <DatePicker value={form.planEndDate} onChange={(e) => set({ planEndDate: e.target.value })} placeholder="תאריך סיום" />
                <span className="ncpp-hint">בתאריך זה הקליניקה תחזור אוטומטית לניסיון</span>
              </label>
            </div>
          )}
        </>
      )}

      <button type="button" className="ncpp-btn ncpp-btn-primary" onClick={() => save()} disabled={busy}>
        <FloppyDisk size={16} />
        {busy ? "שומר…" : "שמור סטטוס תשלום"}
      </button>

      <ConfirmDialog
        isOpen={confirmLeft}
        onClose={() => setConfirmLeft(false)}
        onConfirm={handleConfirmLeft}
        title="סימון כעזב"
        message={`לסמן את "${clinic.name || clinicId}" כעזב? רשומות התשלום נשמרות; המסלול לא משתנה אוטומטית — תוצע הורדה לניסיון.`}
        confirmText="סמן כעזב"
        type="warning"
      />
    </section>
  );
}

export default PaymentStatusCard;
